<template>
  <v-main>
    <v-container
      id="stripe-payment-container"
      fluid
      tag="section"
    >
      <base-material-card
        icon="mdi-credit-card-outline"
        class="px-5 py-3"
      >
        <div class="page-message" v-if="errorMessages.length || successMessages.length || warningMessages.length">
          <shared-page-messages
            :error-messages="errorMessages"
            :success-messages="successMessages"
            :warning-messages="warningMessages"
          />
        </div>

        <v-card-text v-if="status === 'success' && invoices !== null && paymentResult !== null">

          <v-row>
            <v-col cols="12" class="text-center">
              <v-icon color="success" size="64">mdi-check-circle-outline</v-icon>
              <p class="text-h6 mt-4">Payment Successful!</p>
              <p class="p-5">Your Payment has been processed successfully.</p>

              <v-row class="text-left">
                <v-col class="font-weight-bold" cols="12" lg="3">
                  Member:
                </v-col>
                <v-col cols="12" lg="9">
                  {{ name }}
                </v-col>
              </v-row>

              <v-row class="text-left">
                <v-col class="font-weight-bold" cols="12" lg="3">
                  Payment Date:
                </v-col>
                <v-col cols="12" lg="9">
                  {{ paymentResult.paymentDate | formatLocalDateTimeWithSecs }}
                </v-col>
              </v-row>

              <v-row class="text-left">
                <v-col class="font-weight-bold" cols="12" lg="3">
                  Transaction ID:
                </v-col>
                <v-col cols="12" lg="9">
                  {{paymentResult.paymentId}}
                </v-col>
              </v-row>

              <v-row class="text-left">
                <v-col class="font-weight-bold" cols="12" lg="3">
                  Paid Item(s):
                </v-col>
                <v-col cols="12" lg="9">

                  <v-row v-for="payment in paymentResultDetails" :key="payment.invoiceId">
                    <v-col cols="9">
                      {{ payment.invoiceId }} {{ payment.description }}
                    </v-col>
                    <v-col cols="3" class="text-right">
                      {{ payment.amount | formatCurrency }}
                    </v-col>
                  </v-row>

                </v-col>
              </v-row>

              <v-divider class="mt-5"></v-divider>

              <v-row class="text-left mt-2">
                <v-col class="font-weight-bold" cols="9">
                  Total Paid:
                </v-col>
                <v-col cols="3" class="font-weight-bold text-right">
                  {{paymentResult.amount | formatCurrencyUSD}}
                </v-col>
              </v-row>



              <v-btn color="primary" :to="{ name: 'view_invoices' }" class="mt-10">Go to My Invoices</v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text v-else-if="status === 'cancelled'">
          <v-row justify="center">
            <v-col cols="12" sm="6" class="text-center">
              <v-icon color="warning" size="64">mdi-close-circle-outline</v-icon>
              <p class="text-h6 mt-4">Payment Cancelled</p>
              <p>Your payment was not completed. You can try again below.</p>
              <v-btn color="primary" @click="retryPayment" class="mt-2">Try Again</v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text v-else-if="invoicesWithBalance.length === 0">
          <p class="py-5">You don't have any outstanding invoices.</p>
        </v-card-text>

        <v-card-text v-else-if="invoicesWithBalance.length > 0">
          <p class="py-5">Un-check the invoices you do not wish to pay now. At least one invoice must be selected.
            Dates in red indicate overdue invoices.
          </p>

          <v-form ref="paymentForm" @submit.prevent="startCheckout" lazy-validation>
            <v-divider class="my-3"></v-divider>

            <template v-for="(invoice, index) in invoicesWithBalance">
              <v-container :key="invoice.id" class="pa-0">
                <v-row dense>
                  <v-col cols="3" sm="3" class="body-1">
                    <div class="text-body-2 py-2 invoice-item-label">Invoice #:</div>
                    <router-link :to="{ name: 'view_invoice', params: {id: invoice.id} }">
                      {{invoice.id}}
                    </router-link>
                  </v-col>
                  <v-col cols="4" sm="4" class="body-1">
                    <div class="text-body-2 py-2 invoice-item-label">Due Date:</div>
                    <span :class="invoice.status === 'OVERDUE' ? 'invoice-overdue' : ''">
                      {{invoice.dueDate | formatLocalDate}}
                    </span>
                  </v-col>
                  <v-col cols="3" sm="3" class="body-1">
                    <div class="text-body-2 py-2 invoice-item-label">Balance:</div>
                    {{invoice.balance | formatCurrency}}
                  </v-col>
                  <v-col cols="2" sm="2" class="mt-1">
                    <label class="v-label text-body-2 pay-invoice-label" :for="`pay-invoice-${invoice.id}`">Pay:</label>
                    <v-checkbox
                        v-model="invoicePayments"
                        :value="invoice.id"
                        class="ma-0 right-input"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="10" sm="10" class="body-1">
                    <div class="text-body-2 invoice-item-label">Description:</div>
                    <span v-if="invoice.items.length > 0">
                      {{invoice.items[0].description}}
                    </span>
                    <span v-else>Missing Item Description</span>
                  </v-col>

                </v-row>
                <v-divider :key="`divider-${index}`" class="mt-3"></v-divider>

              </v-container>
            </template>

            <v-spacer></v-spacer>

            <v-row class="py-3">
              <v-col cols="8">
                Total Amount Due:
              </v-col>
              <v-col cols="4" class="text-right">
                <strong>{{ totalAmountDue | formatCurrencyUSD }}</strong>
              </v-col>
            </v-row>
            <v-divider class="mb-3"></v-divider>

            <v-row class="py-3">
              <v-col cols="8">
                <strong>Total Payment Amount:</strong>
              </v-col>
              <v-col cols="4" class="text-right">
                <strong>{{ totalPaymentAmount | formatCurrencyUSD }}</strong>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6" sm="6">
                <v-btn
                  color="primary"
                  dark
                  type="submit"
                  id="checkoutBtn"
                  :loading="loading"
                  :disabled="loading"
                >
                  <v-icon left>mdi-credit-card-outline</v-icon>
                  Pay {{ totalPaymentAmount | formatCurrencyUSD }}
                </v-btn>

                <p class="text-body-2 grey--text py-3">
                  You will be redirected to Stripe's secure checkout to complete your payment.
                </p>

              </v-col>


            </v-row>
          </v-form>
        </v-card-text>

      </base-material-card>
    </v-container>
  </v-main>
</template>

<script>
import api from '@/services/api'
import { mapGetters } from 'vuex'
import SharedPageMessages from '@/views/components/shared/PageMessages.vue'
import validation from '@/utils/validation'
import constants from '@/utils/constants'

export default {
  name: 'StripePaymentView',

  components: { SharedPageMessages },

  async created() {
    this.userId = this.getUserId
    this.name = this.getName
    this.sessionId = this.$route.query.sessionId
    this.status = this.$route.query.status

    if (this.status === constants.STRIPE_STATUS_SUCCESS || this.status === constants.STRIPE_STATUS_CANCELLED) {
      if (this.sessionId !== undefined) {
        await this.handlePaymentResult()
        if (this.status === constants.STRIPE_STATUS_SUCCESS) {
          const invoiceFilters = {
            invoiceNumbers: Object.keys(this.paymentResult.invoicePayments).join(',')
          }
          await this.fetchInvoices(invoiceFilters)
        }
      }
    } else {
      const invoiceFilters = {
        statuses: 'OPEN,OVERDUE'
      }
      await this.fetchInvoices(invoiceFilters)
    }
  },

  computed: {
    ...mapGetters({
      getUserId: 'getUserId',
      getName: 'getName',
    }),
    invoicesWithBalance() {
      if (this.invoices) {
        return this.invoices.filter((inv) => inv.balance > 0)
      }
      return []
    },
    totalPaymentAmount() {
      let total = 0
      this.invoicePayments.forEach(invId => {
        total += this.invoices.find(inv => inv.id === invId).balance
      })
      return total
    },
    totalAmountDue() {
      return this.invoices.reduce((sum, inv) => sum + inv.balance, 0)
    },
    paymentResultDetails() {
      const results = []

      for (const invoice of this.invoices) {
        results.push({
          invoiceId: invoice.id,
          description: invoice.items[0].description,
          amount: this.paymentResult.invoicePayments[invoice.id].toFixed(2),
        })
      }

      return results
    }
  },

  watch: {
    invoice(invoice) {
      if (invoice) {
        this.amountDue = invoice.balance.toFixed(2)
        this.itemDescription = this.invoice.items[0].description
        this.paymentAmount = this.amountDue
      }
    }
  },

  methods: {
    async fetchInvoices(invoiceFilters) {
      return api
          .fetchInvoices(this.userId, invoiceFilters)
          .then(response => {
            this.invoices = response.data
            this.buildForm()
          })
          .catch(err => {
            this.handleError(err)
          })
    },

    buildForm() {
      this.invoicePayments = this.invoices.filter(inv => inv.balance > 0)
          .map(inv => inv.id)
    },

    async handlePaymentResult() {
      const payload = {
        sessionId: this.sessionId
      }

      return api
          .handleStripePaymentResult(this.userId, this.invoiceId, payload)
          .then(response => {
            this.paymentResult = response.data
          })
          .catch(err => {
            this.handleError(err)
          })
    },

    handleError: function (err) {
      let errorMessage = api.parseResponseError(err)
      this.errorMessages.push(errorMessage)
    },

    buildPayload() {
      let payload = []
      this.invoicePayments.forEach(invId => {
        const balance = this.invoices.find(inv => invId === inv.id).balance
        if (balance > 0) {
          payload.push({
            invoiceId: invId,
            amount: Math.round(balance * 100)
          })
        }
      })
      return payload
    },

    validate() {
      this.errorMessages = []
      if (!this.$refs.paymentForm.validate()) {
        return false
      }

      if (this.invoicePayments.length === 0) {
        this.errorMessages.push("You must select at least one invoice to pay")
        return false
      }

      if (this.totalPaymentAmount <= 0) {
        this.errorMessages.push("Total payment amount must be more than 0")
        return false
      }

      return true
    },

    async startCheckout() {
      if (!this.validate()) return

      this.loading = true
      this.errorMessages = []
      const payload = this.buildPayload()

      try {
        const response = await api.createStripeCheckoutSession(this.getUserId, payload)
        window.location.href = response.data.checkoutUrl
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    retryPayment() {
      window.location.href = '/pay'
    }
  },

  data() {
    return {
      loading: false,
      status: null,
      sessionId: null,
      invoiceId: null,
      invoice: null,
      invoices: null,
      amountDue: null,
      invoicePayments: [],
      paymentAmount: null,
      itemDescription: null,
      paymentResult: null,
      errorMessages: [],
      successMessages: [],
      warningMessages: [],
      rules: {
        totalPayment: [
          v => !!v || 'Required',
          v => validation.isCurrency(v) || 'Must be a valid dollar amount (e.g. 25.00)',
          v => parseFloat(v) > 1 || 'Must be greater than 1.00',
          v => validation.isLessThanOrEqual(v, this.amountDue) || 'Cannot be more than the balance due',
        ]
      }
    }
  }
}
</script>

<style lang="sass" scoped>
  .invoice-item-label, .pay-invoice-label
    color: #999

  .invoice-overdue
    color: #C00

</style>
